import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CodeIcon, RocketIcon, UsersIcon, ZapIcon } from 'lucide-react';
export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const skills = ['JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'SQL', 'Git', 'AWS', 'REST APIs', 'GraphQL', 'Docker', 'CI/CD'];
  const highlights = [{
    icon: CodeIcon,
    label: 'Clean Code',
    color: 'from-blue-500 to-cyan-500'
  }, {
    icon: RocketIcon,
    label: 'Fast Delivery',
    color: 'from-purple-500 to-pink-500'
  }, {
    icon: UsersIcon,
    label: 'Team Player',
    color: 'from-orange-500 to-red-500'
  }, {
    icon: ZapIcon,
    label: 'Innovation',
    color: 'from-amber-500 to-yellow-500'
  }];
  return <section id="about" className="relative py-32 px-6 bg-white overflow-hidden" data-id="element-1">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2" data-id="element-2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full blur-3xl opacity-30 translate-y-1/2 -translate-x-1/2" data-id="element-3" />

      <div className="max-w-6xl mx-auto relative z-10" ref={ref} data-id="element-4">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-5">
          <div className="text-center mb-16" data-id="element-6">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={isInView ? {
            opacity: 1,
            scale: 1
          } : {}} transition={{
            delay: 0.2,
            duration: 0.6
          }} className="inline-block" data-id="element-7">
              <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 mb-4" data-id="element-8">
                About Me
              </h2>
              <div className="h-2 w-32 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full mx-auto" data-id="element-9" />
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16" data-id="element-10">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            delay: 0.3,
            duration: 0.6
          }} className="space-y-6 text-lg text-slate-700 leading-relaxed" data-id="element-11">
              <p className="text-xl font-semibold text-slate-900" data-id="element-12">
                I'm a software engineer with a passion for creating meaningful
                digital experiences.
              </p>
              <p data-id="element-13">
                My journey in tech has been driven by curiosity and a desire to
                solve real-world problems through elegant code. I believe in the
                power of technology to transform ideas into reality.
              </p>
              <p data-id="element-14">
                With experience across the full stack, I specialize in building
                responsive web applications that prioritize user experience and
                performance. Clean, maintainable code isn't just a
                preference—it's a commitment.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            delay: 0.4,
            duration: 0.6
          }} className="grid grid-cols-2 gap-4" data-id="element-15">
              {highlights.map((item, index) => <motion.div key={item.label} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              delay: 0.5 + index * 0.1,
              duration: 0.5
            }} className="group relative p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 hover:shadow-xl transition-all duration-300" data-id="element-16">
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${item.color} mb-3`} data-id="element-17">
                    <item.icon className="text-white" size={24} data-id="element-18" />
                  </div>
                  <p className="font-bold text-slate-900" data-id="element-19">{item.label}</p>
                </motion.div>)}
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.6,
          duration: 0.6
        }} data-id="element-20">
            <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-21">
              Skills & Technologies
            </h3>
            <div className="flex flex-wrap gap-3 justify-center" data-id="element-22">
              {skills.map((skill, index) => <motion.span key={skill} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              duration: 0.3,
              delay: 0.7 + index * 0.05
            }} className="group relative px-6 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 text-indigo-900 rounded-full text-sm font-bold hover:border-indigo-400 hover:shadow-lg transition-all duration-300" data-id="element-23">
                  {skill}
                </motion.span>)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}